import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Image, Video, Play, X, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Gallery = () => {
  const [media, setMedia] = useState([]);
  const [articles, setArticles] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  const loadContent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [mediaRes, articlesRes] = await Promise.all([
        axios.get(`${API}/media`),
        axios.get(`${API}/articles?published_only=true`)
      ]);
      setMedia(mediaRes.data);
      setArticles(articlesRes.data);
      setImageErrors({});
    } catch (err) {
      console.error('Error loading content:', err);
      setError('Kunne ikke indlæse indhold');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContent();
  }, [loadContent]);

  const handleImageError = (mediaId) => {
    setImageErrors(prev => ({ ...prev, [mediaId]: true }));
  };

  const filteredMedia = activeFilter === 'all' 
    ? media 
    : media.filter(m => m.media_type === activeFilter);

  const currentIndex = selectedMedia ? filteredMedia.findIndex(m => m.id === selectedMedia.id) : -1;

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setSelectedMedia(filteredMedia[currentIndex - 1]);
    }
  }, [currentIndex, filteredMedia]);

  const goToNext = useCallback(() => {
    if (currentIndex < filteredMedia.length - 1) {
      setSelectedMedia(filteredMedia[currentIndex + 1]);
    }
  }, [currentIndex, filteredMedia]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedMedia) return;
      if (e.key === 'ArrowLeft') goToPrevious();
      if (e.key === 'ArrowRight') goToNext();
      if (e.key === 'Escape') setSelectedMedia(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedMedia, goToPrevious, goToNext]);

  if (loading) {
    return (
      <section id="galleri" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-48 mx-auto mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-64 mx-auto"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="galleri" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <button 
            onClick={loadContent}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a472a] text-white rounded-lg hover:bg-[#2d5a3d]"
          >
            <RefreshCw className="w-4 h-4" />
            Prøv igen
          </button>
        </div>
      </section>
    );
  }

  // Don't render if no content
  if (media.length === 0 && articles.length === 0) {
    return null;
  }

  return (
    <section id="galleri" className="py-24 bg-white" aria-labelledby="gallery-heading">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <header className="text-center mb-16">
          <span className="inline-block text-[#1a472a] font-semibold text-sm uppercase tracking-wider mb-4">
            Galleri & Nyheder
          </span>
          <h2 id="gallery-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Billeder og historier
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Se billeder og videoer fra tidligere juletræsfester og læs de seneste nyheder.
          </p>
        </header>

        {/* Articles Section */}
        {articles.length > 0 && (
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Seneste nyheder</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {articles.map((article) => (
                <article
                  key={article.id}
                  className="group bg-[#faf7f2] rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
                  onClick={() => setSelectedArticle(article)}
                >
                  {article.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img
                        src={article.featured_image.startsWith('/') ? `${BACKEND_URL}${article.featured_image}` : article.featured_image}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <time className="text-sm text-[#1a472a] font-medium">
                      {new Date(article.created_at).toLocaleDateString('da-DK', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric'
                      })}
                    </time>
                    <h4 className="text-xl font-bold text-gray-900 mt-2 mb-3 group-hover:text-[#1a472a] transition-colors">
                      {article.title}
                    </h4>
                    <p className="text-gray-600 line-clamp-3">
                      {article.excerpt || article.content.substring(0, 150)}...
                    </p>
                    <span className="inline-flex items-center gap-1 text-[#1a472a] font-semibold mt-4 group-hover:underline">
                      Læs mere <span aria-hidden="true">→</span>
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </div>
        )}

        {/* Media Section */}
        {media.length > 0 && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
              <h3 className="text-2xl font-bold text-gray-900">Billeder & Videoer</h3>
              
              {/* Filter Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveFilter('all')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeFilter === 'all'
                      ? 'bg-[#1a472a] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Alle
                </button>
                <button
                  onClick={() => setActiveFilter('image')}
                  className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeFilter === 'image'
                      ? 'bg-[#1a472a] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Image className="w-4 h-4" /> Billeder
                </button>
                <button
                  onClick={() => setActiveFilter('video')}
                  className={`flex items-center gap-1 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    activeFilter === 'video'
                      ? 'bg-[#1a472a] text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Video className="w-4 h-4" /> Videoer
                </button>
              </div>
            </div>

            {/* Media Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filteredMedia.map((item) => (
                <div
                  key={item.id}
                  className="group relative aspect-square rounded-2xl overflow-hidden cursor-pointer shadow-sm hover:shadow-lg transition-shadow bg-gray-100"
                  onClick={() => !imageErrors[item.id] && setSelectedMedia(item)}
                >
                  {item.media_type === 'image' ? (
                    imageErrors[item.id] ? (
                      <div className="w-full h-full flex items-center justify-center bg-gray-200">
                        <Image className="w-12 h-12 text-gray-400" />
                      </div>
                    ) : (
                      <img
                        src={`${BACKEND_URL}${item.url}?t=${new Date(item.created_at).getTime()}`}
                        alt={item.title || 'Galleri billede'}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                        onError={() => handleImageError(item.id)}
                      />
                    )
                  ) : (
                    <div className="relative w-full h-full bg-gray-900">
                      <video
                        src={`${BACKEND_URL}${item.url}`}
                        className="w-full h-full object-cover"
                        muted
                        onError={() => handleImageError(item.id)}
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                        <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                          <Play className="w-8 h-8 text-[#1a472a] ml-1" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {filteredMedia.length === 0 && (
              <p className="text-center text-gray-500 py-8">
                Ingen {activeFilter === 'image' ? 'billeder' : 'videoer'} fundet
              </p>
            )}
          </div>
        )}
      </div>

      {/* Media Lightbox */}
      {selectedMedia && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
          onClick={() => setSelectedMedia(null)}
        >
          <button
            className="absolute top-4 right-4 p-3 text-white/80 hover:text-white hover:bg-white/10 rounded-full z-10 transition-colors"
            onClick={() => setSelectedMedia(null)}
            aria-label="Luk"
          >
            <X className="w-8 h-8" />
          </button>

          {currentIndex > 0 && (
            <button
              className="absolute left-4 p-3 text-white/80 hover:text-white hover:bg-white/10 rounded-full z-10 transition-colors"
              onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
              aria-label="Forrige"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
          )}

          {currentIndex < filteredMedia.length - 1 && (
            <button
              className="absolute right-4 p-3 text-white/80 hover:text-white hover:bg-white/10 rounded-full z-10 transition-colors"
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              aria-label="Næste"
            >
              <ChevronRight className="w-10 h-10" />
            </button>
          )}

          <div
            className="flex items-center justify-center w-full h-full max-w-[95vw] max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {selectedMedia.media_type === 'image' ? (
              <img
                src={`${BACKEND_URL}${selectedMedia.url}?t=${new Date(selectedMedia.created_at).getTime()}`}
                alt={selectedMedia.title || 'Galleri billede'}
                className="max-w-full max-h-[85vh] w-auto h-auto object-contain rounded-lg shadow-2xl"
                style={{ maxWidth: '95vw' }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <video
                src={`${BACKEND_URL}${selectedMedia.url}`}
                className="max-w-full max-h-[85vh] w-auto h-auto rounded-lg shadow-2xl"
                style={{ maxWidth: '95vw' }}
                controls
                autoPlay
              />
            )}
          </div>

          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/60 text-sm bg-black/50 px-4 py-2 rounded-full">
            {currentIndex + 1} / {filteredMedia.length}
          </div>
        </div>
      )}

      {/* Article Modal */}
      {selectedArticle && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setSelectedArticle(null)}
        >
          <article
            className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto my-8"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="absolute top-4 right-4 p-2 bg-white rounded-full shadow-lg text-gray-600 hover:text-gray-900 z-10"
              onClick={() => setSelectedArticle(null)}
              aria-label="Luk"
            >
              <X className="w-6 h-6" />
            </button>

            {selectedArticle.featured_image && (
              <div className="aspect-video overflow-hidden rounded-t-3xl">
                <img
                  src={selectedArticle.featured_image.startsWith('/') ? `${BACKEND_URL}${selectedArticle.featured_image}` : selectedArticle.featured_image}
                  alt={selectedArticle.title}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            <div className="p-8 sm:p-12">
              <time className="text-sm text-[#1a472a] font-medium">
                {new Date(selectedArticle.created_at).toLocaleDateString('da-DK', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </time>
              <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-6">
                {selectedArticle.title}
              </h2>
              <div className="prose prose-lg max-w-none text-gray-600">
                {selectedArticle.content.split('\n').map((paragraph, index) => (
                  <p key={index} className="mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </article>
        </div>
      )}
    </section>
  );
};

export default Gallery;
